import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { renderPieChartTooltip } from '../../Tooltip';
import {
  PIE_CHART_COLORS,
  PIE_RADIUS,
  PIE_CENTER,
  PIE_BORDER_RADIUS,
  PIE_BORDER_WIDTH,
  PIE_BORDER_COLOR,
  PIE_MIN_ANGLE,
  PIE_LABEL_LINE_LENGTH,
  PIE_LEGEND_BOTTOM,
  PIE_LEGEND_ITEM_GAP,
} from '../constants';

/**
 * 格式化饼图标签
 * @param params ECharts 参数
 * @param isHighlight 是否高亮
 * @returns 格式化后的标签字符串
 */
const formatPieChartLabel = (params: any, isHighlight: boolean): string => {
  const name = params?.data?.name || '';
  const percent = params?.percent || 0;
  const contextStyle = isHighlight ? 'contextbold' : 'context';
  return `{${contextStyle}|${name}: ${percent.toFixed(1)}%}\n{hr|}`;
};

/**
 * 生成饼图标签格式化函数
 * @param highlightId 高亮的数据项 ID
 * @param customFormatter 自定义格式化函数
 * @returns 标签格式化函数
 */
const getPieChartLabelFormatter = (
  highlightId?: string,
  customFormatter?: ((params: any) => string) | null,
): ((params: any) => string) => {
  if (customFormatter) {
    return customFormatter;
  }

  return (params: any): string => {
    const isHighlight = highlightId ? params?.data?.id === highlightId : false;
    return formatPieChartLabel(params, isHighlight);
  };
};

/**
 * 生成饼图图例格式化函数
 * @param data 图表数据
 * @param highlightId 高亮的数据项 ID
 * @param customFormatter 自定义格式化函数
 * @returns 图例格式化函数
 */
const getPieChartLegendFormatter = (
  data: IPieChartData[],
  highlightId?: string,
  customFormatter?: ((name: string) => string) | null,
): ((name: string) => string) => {
  if (customFormatter) {
    return customFormatter;
  }

  return (name: string): string => {
    const item = data.find((d) => d.name === name);
    const isHighlight = highlightId && item?.id === highlightId;
    const styleKey = isHighlight ? 'highlightName' : 'normalName';
    return `{${styleKey}|${name}}`;
  };
};

/**
 * 生成饼图 series 数据
 * @param data 图表数据
 * @param highlightId 高亮的数据项 ID
 * @param labelFormatter 标签格式化函数
 * @returns series 数据数组
 */
const getPieChartSeriesData = (
  data: IPieChartData[],
  highlightId?: string,
  labelFormatter?: ((params: any) => string) | null,
): any[] => {
  return data.map((item, index) => ({
    name: item.name,
    value: item.value,
    id: item.id || item.name,
    payload: item.payload || {},
    itemStyle: {
      color: PIE_CHART_COLORS[index % PIE_CHART_COLORS.length],
    },
  }));
};

/**
 * 生成饼图 Tooltip 配置
 * @param data 图表数据
 * @param metricName 指标名称
 * @param timeRange 时间范围
 * @param valueFormatter 数值格式化函数
 * @param customTooltipFormatter 自定义 Tooltip 格式化函数
 * @returns Tooltip 配置
 */
const getPieChartTooltipConfig = (
  data: IPieChartData[],
  metricName?: string,
  timeRange?: string,
  valueFormatter?: TValueFormatter | null,
  customTooltipFormatter?: ((params: any) => string) | null,
): any => {
  const valFormatter = valueFormatter || formatKMBWithPlaceholder;

  // 如果提供了自定义 Tooltip 格式化函数，直接使用
  if (customTooltipFormatter) {
    return {
      trigger: 'item',
      padding: 0,
      borderWidth: 0,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      confine: true,
      hideDelay: 200,
      triggerOn: 'mousemove',
      formatter: customTooltipFormatter,
    };
  }

  return {
    trigger: 'item',
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'rgba(255, 255, 255, 0)',
    confine: true,
    hideDelay: 200,
    triggerOn: 'mousemove',
    formatter: (params: any) => {
      if (!params) {
        return '';
      }

      const dataItem = {
        color: params.color || PIE_CHART_COLORS[params.dataIndex % PIE_CHART_COLORS.length],
        name: params.name || '',
        value: valFormatter(params.value),
        percent: `${(params.percent || 0).toFixed(1)}%`,
        payload: params.data?.payload || {},
      };

      return renderPieChartTooltip({
        metricName,
        timeRange,
        dataItem,
      });
    },
  };
};

/**
 * 生成饼图图例配置
 * @param data 图表数据
 * @param showLegend 是否显示图例
 * @param highlightId 高亮的数据项 ID
 * @param legendFormatter 图例格式化函数
 * @returns 图例配置
 */
const getPieChartLegendConfig = (
  data: IPieChartData[],
  showLegend = true,
  highlightId?: string,
  legendFormatter?: ((name: string) => string) | null,
): any => {
  if (!showLegend) {
    return { show: false };
  }

  return {
    type: 'scroll',
    orient: 'horizontal',
    pageIconColor: '#000',
    selectedMode: true,
    pageIconSize: 12,
    pageTextStyle: {
      color: '#000',
    },
    show: true,
    icon: 'circle',
    itemWidth: 20,
    itemHeight: 10,
    bottom: PIE_LEGEND_BOTTOM,
    left: 'center',
    itemGap: PIE_LEGEND_ITEM_GAP,
    itemStyle: {
      opacity: 1,
    },
    textStyle: {
      padding: [0, 0, 0, -7],
      rich: {
        normalName: {
          color: '#888888',
          fontSize: 12,
        },
        highlightName: {
          color: '#262832',
          fontSize: 12,
          fontWeight: 'bold',
        },
      },
    },
    formatter: getPieChartLegendFormatter(data, highlightId, legendFormatter),
  };
};

/**
 * 生成饼图标题配置
 * @param centerTitle 中心标题
 * @param configs 其他配置
 * @param configs.left 中心标题距离左侧的距离
 * @returns 标题配置
 */
const getPieChartTitleConfig = (
  centerTitle?: string,
  configs?: { top: number | string; left: number | string },
): any => {
  if (!centerTitle) {
    return { show: false };
  }

  return {
    text: centerTitle,
    top: configs?.top ?? '50%',
    left: configs?.left ?? '50%',
    textAlign: 'center',
    textVerticalAlign: 'middle',
    textStyle: {
      lineHeight: 16,
      fontWeight: 600,
      fontSize: 12,
      color: '#262832',
    },
  };
};

/**
 * 获取饼状图动态配置
 * @param params 配置参数
 * @returns ECharts option 对象
 */
export const getPieChartDynamicOption = (params: IGetPieChartDynamicOptionParams): any => {
  const {
    data,
    highlightId,
    metricName,
    timeRange,
    centerTitle,
    centerTitleConfigs,
    showLabel = true,
    showLegend = true,
    tooltipFormatter,
    valueFormatter,
    labelFormatter,
    legendFormatter,
  } = params;

  if (!data || data.length === 0) {
    return {
      legend: { show: false },
      series: [],
    };
  }

  // 生成 series 数据
  const seriesData = getPieChartSeriesData(data, highlightId, labelFormatter);

  // 生成标签格式化函数
  const labelFormatterFn = getPieChartLabelFormatter(highlightId, labelFormatter);

  return {
    color: PIE_CHART_COLORS,
    title: getPieChartTitleConfig(centerTitle, centerTitleConfigs),
    tooltip: getPieChartTooltipConfig(data, metricName, timeRange, valueFormatter, tooltipFormatter),
    legend: getPieChartLegendConfig(data, showLegend, highlightId, legendFormatter),
    series: [
      {
        type: 'pie',
        avoidLabelOverlap: true,
        radius: PIE_RADIUS,
        center: PIE_CENTER,
        minAngle: PIE_MIN_ANGLE,
        data: seriesData,
        itemStyle: {
          borderRadius: PIE_BORDER_RADIUS,
          borderColor: PIE_BORDER_COLOR,
          borderWidth: PIE_BORDER_WIDTH,
        },
        label: {
          show: showLabel,
          position: 'outside',
          alignTo: 'edge',
          edgeDistance: 10,
          bleedMargin: 5,
          fontSize: 12,
          color: '#262832',
          formatter: labelFormatterFn,
          padding: [0, -6],
          rich: {
            per: {
              padding: [4, 0],
              align: 'left',
              opacity: 1,
            },
            context: {
              padding: [4, 0],
              align: 'left',
              opacity: 1,
            },
            contextbold: {
              padding: [4, 0],
              align: 'left',
              opacity: 1,
              fontWeight: 'bold',
            },
            hr: {
              width: '100%',
              height: 0,
              borderWidth: 2,
              borderColor: 'auto',
              opacity: 1,
            },
            a: {
              lineHeight: 20,
              align: 'center',
              color: 'transparent',
            },
          },
        },
        labelLine: {
          length: PIE_LABEL_LINE_LENGTH,
          length2: PIE_LABEL_LINE_LENGTH * 0.8,
          showAbove: false,
          maxSurfaceAngle: 80,
          lineStyle: {
            width: 2,
          },
        },
      },
    ],
    graphic: [
      {
        type: 'text',
      },
    ],
  };
};
